import apiService from './api';
import { unwrap } from '../utils/apiResponse';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SensorType {
  type_key: string;
  display_name: string;
  unit: string | null;
  value_schema: Record<string, unknown> | null;
  icon: string | null;
}

export type SensorTransport = 'http' | 'mqtt' | 'espnow_peer';

export interface SensorDevice {
  device_id: string;
  organisation_id: number | null;
  type_key: string;
  name: string | null;
  transport: SensorTransport;
  field_controller_id: string | null;
  parent_sensor_id: string | null;
  farm_id: number | null;
  lat: number | null;
  lng: number | null;
  config: Record<string, unknown>;
  firmware_version: string | null;
  last_seen: string | null;
  online: boolean;
  created_at: string;
  updated_at: string;
  api_key_hash?: string | null;

  // joined fields
  type_display_name?: string;
  type_unit?: string | null;
  type_icon?: string | null;
  organisation_name?: string;

  // latest reading (from getDevicesByOrg)
  latest_reading_time?: string | null;
  latest_value?: number | null;
  latest_raw?: Record<string, unknown> | null;

  // channel data (multi-channel devices)
  channels?: SensorChannel[];
  latestReadings?: SensorReading[];
}

export interface SensorReading {
  id: number;
  reading_time: string;
  value: number | null;
  raw: Record<string, unknown> | null;
  channel_index?: number;
  channel_kind?: string;
}

export interface SensorChannel {
  id: number;
  device_id: string;
  channel_index: number;
  channel_kind: 'analog' | 'digital';
  type_key: string;
  name: string | null;
  config: Record<string, unknown>;
  enabled: boolean;
  created_at: string;
  updated_at: string;
  // joined
  type_display_name?: string;
  type_unit?: string | null;
  type_icon?: string | null;
}

export interface TankStats {
  current_liters: number | null;
  capacity_liters: number;
  percent_full: number | null;
  avg_daily_usage_liters: number;
  days_to_empty: number | null;
  daily_usage: Array<{ date: string; liters: number }>;
}

export interface PreRegisterDeviceInput {
  device_id: string;
  type_key: string;
  transport?: SensorTransport;
  name?: string | null;
  organisation_id?: number | null;
  field_controller_id?: string | null;
  parent_sensor_id?: string | null;
  farm_id?: number | null;
  config?: Record<string, unknown>;
}

export interface UpdateDeviceInput {
  name?: string | null;
  farm_id?: number | null;
  lat?: number | null;
  lng?: number | null;
  config?: Record<string, unknown> | null;
}

// ─── User-facing endpoints ────────────────────────────────────────────────────

async function listTypes(): Promise<SensorType[]> {
  const res = await apiService.get<SensorType[]>('/sensor-devices/types');
  return unwrap(res);
}

async function listDevices(): Promise<SensorDevice[]> {
  const res = await apiService.get<SensorDevice[]>('/sensor-devices');
  return unwrap(res);
}

async function getDevice(deviceId: string): Promise<SensorDevice> {
  const res = await apiService.get<SensorDevice>(`/sensor-devices/${encodeURIComponent(deviceId)}`);
  return unwrap(res);
}

async function updateDevice(deviceId: string, data: UpdateDeviceInput): Promise<SensorDevice> {
  const res = await apiService.put<SensorDevice>(`/sensor-devices/${encodeURIComponent(deviceId)}`, data);
  return unwrap(res);
}

async function getReadings(
  deviceId: string,
  opts: { startDate?: string; endDate?: string; limit?: number; channelIndex?: number; channelKind?: string } = {}
): Promise<SensorReading[]> {
  const qs = new URLSearchParams();
  if (opts.startDate) qs.set('startDate', opts.startDate);
  if (opts.endDate) qs.set('endDate', opts.endDate);
  if (opts.limit) qs.set('limit', String(opts.limit));
  if (opts.channelIndex !== undefined) qs.set('channel_index', String(opts.channelIndex));
  if (opts.channelKind) qs.set('channel_kind', opts.channelKind);
  const tail = qs.toString() ? `?${qs.toString()}` : '';
  const res = await apiService.get<SensorReading[]>(
    `/sensor-devices/${encodeURIComponent(deviceId)}/readings${tail}`
  );
  return unwrap(res);
}

async function getChannels(deviceId: string): Promise<SensorChannel[]> {
  const res = await apiService.get<SensorChannel[]>(
    `/sensor-devices/${encodeURIComponent(deviceId)}/channels`
  );
  return unwrap(res);
}

async function getLatestPerChannel(deviceId: string): Promise<SensorReading[]> {
  const res = await apiService.get<SensorReading[]>(
    `/sensor-devices/${encodeURIComponent(deviceId)}/latest-per-channel`
  );
  return unwrap(res);
}

async function getTankStats(
  deviceId: string,
  kind: 'analog' | 'digital',
  index: number,
  days = 14
): Promise<TankStats> {
  const res = await apiService.get<TankStats>(
    `/sensor-devices/${encodeURIComponent(deviceId)}/channels/${kind}/${index}/tank-stats?days=${days}`
  );
  return unwrap(res);
}

async function getLatest(deviceId: string): Promise<SensorReading | null> {
  const res = await apiService.get<SensorReading | null>(
    `/sensor-devices/${encodeURIComponent(deviceId)}/latest`
  );
  return unwrap(res);
}

// ─── Admin endpoints ─────────────────────────────────────────────────────────

async function listAllDevices(): Promise<SensorDevice[]> {
  const res = await apiService.get<SensorDevice[]>('/sensor-devices/admin/devices');
  return unwrap(res);
}

async function preRegisterDevice(data: PreRegisterDeviceInput): Promise<SensorDevice> {
  const res = await apiService.post<SensorDevice>('/sensor-devices/admin/devices', data);
  return unwrap(res);
}

async function assignDeviceToOrg(deviceId: string, organisationId: number): Promise<SensorDevice> {
  const res = await apiService.put<SensorDevice>(
    `/sensor-devices/admin/devices/${encodeURIComponent(deviceId)}/assign`,
    { organisation_id: organisationId }
  );
  return unwrap(res);
}

async function unclaimDevice(deviceId: string): Promise<void> {
  await apiService.post(`/sensor-devices/admin/devices/${encodeURIComponent(deviceId)}/unclaim`);
}

async function deleteDevice(deviceId: string): Promise<void> {
  await apiService.delete(`/sensor-devices/admin/devices/${encodeURIComponent(deviceId)}`);
}

async function deleteAllReadings(deviceId: string): Promise<{ deleted: number }> {
  const res = await apiService.delete<{ deleted: number }>(
    `/sensor-devices/${encodeURIComponent(deviceId)}/readings`
  );
  return unwrap(res);
}

export default {
  listTypes,
  listDevices,
  getDevice,
  updateDevice,
  getReadings,
  getLatest,
  getChannels,
  getLatestPerChannel,
  getTankStats,
  listAllDevices,
  preRegisterDevice,
  assignDeviceToOrg,
  unclaimDevice,
  deleteDevice,
  deleteAllReadings,
};
